import { useEffect, useState } from "react";
import { getToken } from "../utils/auth";
import "./AssignCourseOrMcq.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const TYPE_META = {
  course:        { label: "Course",         icon: "📚", color: "#3b82f6", bg: "rgba(59,130,246,0.08)"  },
  mcq:           { label: "MCQ",            icon: "📝", color: "#6366f1", bg: "rgba(99,102,241,0.08)"  },
  assignment:    { label: "Assignment",     icon: "📋", color: "#f59e0b", bg: "rgba(245,158,11,0.08)"  },
  studyMaterial: { label: "Study Material", icon: "📖", color: "#22c55e", bg: "rgba(34,197,94,0.08)"   },
};

export default function AssignCourseOrMcq() {
  const [campuses, setCampuses]           = useState([]);
  const [years, setYears]                 = useState([]);
  const [batches, setBatches]             = useState([]);
  const [campus, setCampus]               = useState("");
  const [year, setYear]                   = useState("");
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [mode, setMode]                   = useState("assign");
  const [assignType, setAssignType]       = useState("");
  const [courses, setCourses]             = useState([]);
  const [mcqs, setMcqs]                   = useState([]);
  const [assignments, setAssignments]     = useState([]);
  const [studyMaterials, setStudyMaterials] = useState([]);
  const [selectedItem, setSelectedItem]   = useState("");
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [students, setStudents]           = useState([]);
  const [loading, setLoading]             = useState(false);
  const [message, setMessage]             = useState(null); // { type, text }
  const [progress, setProgress]           = useState({ current: 0, total: 0 });
  const [showModal, setShowModal]         = useState(false);
  const [showStudentList, setShowStudentList] = useState(false);

  /* ── FETCH CAMPUSES ── */
  useEffect(() => {
    fetch(`${API_BASE}/campus/get`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json()).then(setCampuses);
  }, []);

  /* ── FETCH YEARS ── */
  useEffect(() => {
    if (!campus) return;
    fetch(`${API_BASE}/year/get-by-campus?campus=${encodeURIComponent(campus)}`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json())
      .then(data => { setYears(data); setYear(""); setSelectedBatches([]); });
  }, [campus]);

  /* ── FETCH BATCHES ── */
  useEffect(() => {
    if (!campus || !year) return;
    fetch(`${API_BASE}/batch/get-by-campus-year?campus=${encodeURIComponent(campus)}&year=${year}`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json()).then(setBatches);
  }, [campus, year]);

  /* ── FETCH STUDENTS ── */
  useEffect(() => {
    if (!campus || !year || selectedBatches.length === 0) { setStudents([]); return; }
    const load = async () => {
      let all = [];
      for (const batch of selectedBatches) {
        const res = await fetch(
          `${API_BASE}/api/students/students?college=${encodeURIComponent(campus)}&year=${encodeURIComponent(year)}&batch=${encodeURIComponent(batch)}`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        const data = await res.json();
        all.push(...data);
      }
      setStudents(Array.from(new Map(all.map(s => [s.email, s])).values()));
    };
    load();
  }, [campus, year, selectedBatches]);

  /* ── FETCH RESOURCE LIST ── */
  useEffect(() => {
    setSelectedItem(""); setSelectedPreview(null);
    if (assignType === "course")
      fetch(`${API_BASE}/courses/student`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }, body: "{}" })
        .then(r => r.json()).then(setCourses);
    if (assignType === "mcq")
      fetch(`${API_BASE}/getAllMCQForAdmin`, { headers: { Authorization: `Bearer ${getToken()}` } })
        .then(r => r.json()).then(setMcqs);
    if (assignType === "assignment")
      fetch(`${API_BASE}/api/assignments`, { headers: { Authorization: `Bearer ${getToken()}` } })
        .then(r => r.json()).then(setAssignments);
    if (assignType === "studyMaterial")
      fetch(`${API_BASE}/api/study-material/getAll`, { headers: { Authorization: `Bearer ${getToken()}` } })
        .then(r => r.json())
        .then(data => setStudyMaterials(Array.isArray(data) ? data : data?.data || []));
  }, [assignType]);

  /* ── PREVIEW ── */
  useEffect(() => {
    if (!selectedItem) { setSelectedPreview(null); return; }
    const map = { course: courses, mcq: mcqs, assignment: assignments, studyMaterial: studyMaterials };
    setSelectedPreview((map[assignType] || []).find(i => i._id === selectedItem) || null);
  }, [assignType, selectedItem, courses, mcqs, assignments, studyMaterials]);

  const batchValues   = batches.map(b => b.Batchname || b.batch);
  const toggleAll     = () => setSelectedBatches(selectedBatches.length === batchValues.length ? [] : batchValues);
  const pct           = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  const getItemName = (item) => {
    if (!item) return "";
    return item.name || item.topic || item.course || item.title || "Untitled";
  };

  /* ── SUBMIT ── */
  const handleSubmit = async () => {
    if (!assignType || !selectedItem) { setMessage({ type: "error", text: "Please select a resource type and item." }); return; }
    if (students.length === 0)        { setMessage({ type: "error", text: "No students found for the selected filters." }); return; }

    setLoading(true); setMessage(null);
    setProgress({ current: 0, total: students.length });
    setShowModal(true);

    const endpoints = {
      course:        { assign: "/addCourse",              remove: "/removeCourse",              key: "course" },
      mcq:           { assign: "/addMcq",                 remove: "/removeMcq",                 key: "mcq" },
      assignment:    { assign: "/addAssignmentToUser",    remove: "/removeAssignmentFromUser",   key: "assignmentId" },
      studyMaterial: { assign: "/api/study-material/addtostudent", remove: "/api/study-material/remove", key: "studyMaterialId" },
    };
    const ep = endpoints[assignType];
    const endpoint = mode === "assign" ? ep.assign : ep.remove;
    let ok = 0;

    for (const s of students) {
      try {
        await fetch(`${API_BASE}${endpoint}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify({ email: s.email, [ep.key]: selectedItem }),
        });
        setProgress(p => ({ ...p, current: p.current + 1 }));
        ok++;
      } catch (err) { console.error(err); }
    }

    setMessage({ type: "success", text: `Successfully ${mode === "assign" ? "assigned to" : "removed from"} ${ok} student${ok !== 1 ? "s" : ""}.` });
    setLoading(false);
    setTimeout(() => setShowModal(false), 1800);
  };

  /* ── STEP COMPLETION ── */
  const step1Done = !!campus && !!year;
  const step2Done = selectedBatches.length > 0;
  const step3Done = !!assignType && !!selectedItem;
  const canSubmit = step1Done && step2Done && step3Done && !loading;

  return (
    <div className="acm-page">

      {/* ══ PAGE HEADER ══ */}
      <div className="acm-header">
        <div>
          <h1 className="acm-title">Manage Curriculum</h1>
          <p className="acm-subtitle">Assign or remove resources for student batches</p>
        </div>

        {/* mode toggle */}
        <div className="acm-mode-toggle">
          <button className={`acm-mode-btn ${mode === "assign" ? "acm-mode-active-assign" : ""}`} onClick={() => setMode("assign")}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Assign
          </button>
          <button className={`acm-mode-btn ${mode === "remove" ? "acm-mode-active-remove" : ""}`} onClick={() => setMode("remove")}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Remove
          </button>
        </div>
      </div>

      {/* ══ KPI STRIP ══ */}
      <div className="acm-kpi-strip">
        {[
          { label: "Campuses",         value: campuses.length,       color: "#3b82f6" },
          { label: "Years",            value: years.length,          color: "#6366f1" },
          { label: "Batches Selected", value: selectedBatches.length, color: "#f59e0b" },
          { label: "Students",         value: students.length,       color: "#22c55e" },
        ].map((k, i) => (
          <div key={i} className="acm-kpi">
            <span className="acm-kpi-val" style={{ color: k.color }}>{k.value}</span>
            <span className="acm-kpi-lbl">{k.label}</span>
          </div>
        ))}
      </div>

      {/* ══ TWO-COLUMN LAYOUT ══ */}
      <div className="acm-layout">

        {/* ── LEFT: FORM ── */}
        <div className="acm-form-col">

          {/* STEP 1 — Academic Filter */}
          <div className="acm-section">
            <div className="acm-section-head">
              <div className={`acm-step-badge ${step1Done ? "acm-step-done" : ""}`}>
                {step1Done
                  ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  : "1"}
              </div>
              <div>
                <p className="acm-section-title">Academic Filter</p>
                <p className="acm-section-sub">Select college and year</p>
              </div>
            </div>

            <div className="acm-form-grid">
              <div className="acm-field">
                <label>College / Campus</label>
                <select value={campus} onChange={e => setCampus(e.target.value)}>
                  <option value="">Select College</option>
                  {campuses.map(c => <option key={c._id} value={c.Campusname || c.name}>{c.college || c.name}</option>)}
                </select>
              </div>
              <div className="acm-field">
                <label>Academic Year</label>
                <select value={year} onChange={e => setYear(e.target.value)} disabled={!campus}>
                  <option value="">Select Year</option>
                  {years.map(y => <option key={y._id} value={y.Year || y.year}>{y.Year || y.year}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* STEP 2 — Batches */}
          <div className={`acm-section ${!step1Done ? "acm-section-disabled" : ""}`}>
            <div className="acm-section-head">
              <div className={`acm-step-badge ${step2Done ? "acm-step-done" : ""}`}>
                {step2Done
                  ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  : "2"}
              </div>
              <div>
                <p className="acm-section-title">Select Batches</p>
                <p className="acm-section-sub">{selectedBatches.length > 0 ? `${selectedBatches.length} batch${selectedBatches.length > 1 ? "es" : ""} selected` : "Choose one or more batches"}</p>
              </div>
              {batches.length > 0 && (
                <button className="acm-toggle-all-btn" onClick={toggleAll}>
                  {selectedBatches.length === batchValues.length ? "Clear All" : "Select All"}
                </button>
              )}
            </div>

            {batches.length === 0 && step1Done && (
              <p className="acm-empty-hint">No batches found for the selected college and year.</p>
            )}

            {batches.length > 0 && (
              <div className="acm-batch-grid">
                {batches.map(b => {
                  const val = b.Batchname || b.batch;
                  const active = selectedBatches.includes(val);
                  return (
                    <label key={b._id} className={`acm-batch-chip ${active ? "acm-batch-active" : ""}`}>
                      <input type="checkbox" hidden checked={active}
                        onChange={e => setSelectedBatches(p => e.target.checked ? [...p, val] : p.filter(x => x !== val))} />
                      {active && (
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                      {val}
                    </label>
                  );
                })}
              </div>
            )}

            {selectedBatches.length > 0 && (
              <div className="acm-selected-tags">
                <span className="acm-tags-label">Selected:</span>
                {selectedBatches.map(b => <span key={b} className="acm-tag">{b}</span>)}
              </div>
            )}
          </div>

          {/* STEP 3 — Resource */}
          <div className={`acm-section ${!step2Done ? "acm-section-disabled" : ""}`}>
            <div className="acm-section-head">
              <div className={`acm-step-badge ${step3Done ? "acm-step-done" : ""}`}>
                {step3Done
                  ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  : "3"}
              </div>
              <div>
                <p className="acm-section-title">Select Resource</p>
                <p className="acm-section-sub">Choose what to {mode}</p>
              </div>
            </div>

            {/* type tiles */}
            <div className="acm-type-grid">
              {Object.entries(TYPE_META).map(([key, meta]) => (
                <button
                  key={key}
                  className={`acm-type-tile ${assignType === key ? "acm-type-active" : ""}`}
                  style={assignType === key ? { borderColor: meta.color, background: meta.bg } : {}}
                  onClick={() => setAssignType(key)}
                >
                  <span className="acm-type-icon">{meta.icon}</span>
                  <span className="acm-type-label" style={assignType === key ? { color: meta.color } : {}}>{meta.label}</span>
                </button>
              ))}
            </div>

            {assignType && (
              <div className="acm-field" style={{ marginTop: 16 }}>
                <label>Select {TYPE_META[assignType]?.label}</label>
                <select value={selectedItem} onChange={e => setSelectedItem(e.target.value)}>
                  <option value="">Choose {TYPE_META[assignType]?.label}…</option>
                  {assignType === "course"        && courses.map(c        => <option key={c._id} value={c._id}>{c.name}</option>)}
                  {assignType === "mcq"           && mcqs.map(m           => <option key={m._id} value={m._id}>{m.topic}</option>)}
                  {assignType === "assignment"    && assignments.map(a    => <option key={a._id} value={a._id}>{a.name}</option>)}
                  {assignType === "studyMaterial" && studyMaterials.map(s => <option key={s._id} value={s._id}>{s.course || s.title || "Untitled"}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* ── SUBMIT ── */}
          <button className={`acm-submit-btn ${mode === "remove" ? "acm-submit-remove" : ""}`} onClick={handleSubmit} disabled={!canSubmit}>
            {loading ? (
              <><span className="acm-spinner" /> Processing…</>
            ) : (
              <>
                {mode === "assign" ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                )}
                {mode === "assign" ? "Assign Resource" : "Remove Resource"}
              </>
            )}
          </button>

          {message && (
            <div className={`acm-message ${message.type === "success" ? "acm-msg-success" : "acm-msg-error"}`}>
              {message.type === "success"
                ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>}
              {message.text}
            </div>
          )}
        </div>

        {/* ── RIGHT: PREVIEW PANEL ── */}
        <div className="acm-preview-col">
          <div className="acm-preview-card">
            <p className="acm-preview-heading">Summary</p>

            {/* target info */}
            <div className="acm-preview-section">
              <p className="acm-preview-label">Target</p>
              {campus ? (
                <div className="acm-preview-rows">
                  <div className="acm-preview-row">
                    <span>College</span><strong>{campus}</strong>
                  </div>
                  {year && <div className="acm-preview-row"><span>Year</span><strong>{year}</strong></div>}
                  {selectedBatches.length > 0 && (
                    <div className="acm-preview-row">
                      <span>Batches</span>
                      <div className="acm-preview-tags">
                        {selectedBatches.map(b => <span key={b} className="acm-preview-tag">{b}</span>)}
                      </div>
                    </div>
                  )}
                  {students.length > 0 && (
                    <div className="acm-preview-row">
                      <span>Students</span>
                      <strong className="acm-preview-count">{students.length}</strong>
                    </div>
                  )}
                </div>
              ) : (
                <p className="acm-preview-empty">No target selected yet</p>
              )}
            </div>

            {/* resource info */}
            <div className="acm-preview-section">
              <p className="acm-preview-label">Resource</p>
              {selectedPreview ? (
                <div className="acm-resource-preview">
                  <div className="acm-resource-icon" style={{ background: TYPE_META[assignType]?.bg, color: TYPE_META[assignType]?.color }}>
                    {TYPE_META[assignType]?.icon}
                  </div>
                  <div>
                    <p className="acm-resource-type" style={{ color: TYPE_META[assignType]?.color }}>{TYPE_META[assignType]?.label}</p>
                    <p className="acm-resource-name">{getItemName(selectedPreview)}</p>
                  </div>
                </div>
              ) : (
                <p className="acm-preview-empty">No resource selected yet</p>
              )}
            </div>

            {/* action summary */}
            <div className="acm-preview-section">
              <p className="acm-preview-label">Action</p>
              <div className={`acm-action-badge ${mode === "assign" ? "acm-action-assign" : "acm-action-remove"}`}>
                {mode === "assign" ? "✓ Assign to students" : "− Remove from students"}
              </div>
            </div>

            {/* student list toggle */}
            {students.length > 0 && (
              <div className="acm-preview-section">
                <button className="acm-show-students-btn" onClick={() => setShowStudentList(p => !p)}>
                  {showStudentList ? "Hide" : "Show"} students ({students.length})
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                    style={{ transform: showStudentList ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                {showStudentList && (
                  <div className="acm-student-list">
                    {students.slice(0, 8).map(s => (
                      <div key={s.email} className="acm-student-row">
                        <div className="acm-student-avatar">{(s.name || s.email)[0].toUpperCase()}</div>
                        <div>
                          <p className="acm-student-name">{s.name || "—"}</p>
                          <p className="acm-student-email">{s.email}</p>
                        </div>
                      </div>
                    ))}
                    {students.length > 8 && (
                      <p className="acm-student-more">+{students.length - 8} more students</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* checklist */}
            <div className="acm-checklist">
              {[
                { done: step1Done, label: "College & year selected" },
                { done: step2Done, label: "Batch(es) selected" },
                { done: step3Done, label: "Resource selected" },
              ].map((item, i) => (
                <div key={i} className={`acm-check-row ${item.done ? "acm-check-done" : ""}`}>
                  <div className="acm-check-dot">
                    {item.done && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                  </div>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ══ PROGRESS MODAL ══ */}
      {showModal && (
        <div className="acm-modal-overlay">
          <div className="acm-modal">
            <div className={`acm-modal-icon ${pct === 100 ? "acm-modal-icon-done" : "acm-modal-icon-loading"}`}>
              {pct === 100
                ? <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                : <span className="acm-modal-spinner" />}
            </div>
            <h3 className="acm-modal-title">
              {pct === 100 ? "All Done!" : `${mode === "assign" ? "Assigning" : "Removing"} Resources…`}
            </h3>
            <p className="acm-modal-sub">
              {pct === 100 ? "Resources have been processed successfully." : "Please wait while we update each student."}
            </p>

            <div className="acm-modal-prog-wrap">
              <div className="acm-modal-prog-track">
                <div className="acm-modal-prog-fill" style={{ width: `${pct}%`, background: pct === 100 ? "#22c55e" : "linear-gradient(90deg,#3b82f6,#6366f1)" }} />
              </div>
              <div className="acm-modal-prog-labels">
                <span>{pct}% complete</span>
                <span>{progress.current} / {progress.total} students</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
